import { useMDXComponent } from "@content-collections/mdx/react";
import { IconLink } from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import type React from "react";
import {
	type ComponentProps,
	type ComponentType,
	createContext,
	useContext,
	useMemo,
} from "react";
import { Tweet } from "react-tweet";
import { toast } from "sonner";
import { CodeBlockFigure } from "@/components/mdx/CodeBlockFigure";
import { FAQ } from "@/components/mdx/FAQ";
import { Footnote, FootnoteRef, Footnotes } from "@/components/mdx/Footnotes";
import { OSTab, OSTabs } from "@/components/mdx/OSTabs";
import { Note } from "@/components/ui/note";
import { createHeadingSlugger, slugifyHeading } from "@/lib/heading-slug";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/utils/clipboard";
import { addReferralParam } from "@/utils/external-link";

const HeadingSluggerContext = createContext<ReturnType<
	typeof createHeadingSlugger
> | null>(null);

function CustomTweet({ id }: { id: string }) {
	return (
		<div className="not-prose flex justify-center">
			<Tweet id={id} />
		</div>
	);
}
const linkClassName =
	"text-blue-600 underline underline-offset-2 hover:text-blue-800 transition-colors";

// biome-ignore lint/suspicious/noExplicitAny: Weird types
const CustomLink = (props: any) => {
	const href = props.href;

	if (href.startsWith("/")) {
		return (
			<Link {...props} to={href} className={cn(linkClassName, props.className)}>
				{props.children}
			</Link>
		);
	}

	if (href.startsWith("#")) {
		return <a {...props} className={cn(linkClassName, props.className)} />;
	}

	// External link - add referral parameter
	const externalHref = addReferralParam(href);
	return (
		<a
			{...props}
			href={externalHref}
			target="_blank"
			rel="noopener noreferrer"
			className={cn(linkClassName, props.className)}
		>
			{props.children}
		</a>
	);
};

type MdxComponent = ComponentType<{ components?: Record<string, unknown> }>;

// Eager load so prerender SSR and client hydration render identical HTML.
const mdxModules = import.meta.glob("../../.content-collections/mdx/**/*.js", {
	eager: true,
}) as Record<string, { default: MdxComponent }>;

function resolveMdxComponent(moduleKey: string): MdxComponent | undefined {
	const suffix = `/${moduleKey.replace(/^\//, "")}.js`;
	const path = Object.keys(mdxModules).find((key) => key.endsWith(suffix));
	return path ? mdxModules[path].default : undefined;
}

type MdxFromModuleProps = {
	moduleKey: string;
	components: Record<string, unknown>;
};

function MdxFromModule({ moduleKey, components }: MdxFromModuleProps) {
	const MDXContent = useMemo(() => resolveMdxComponent(moduleKey), [moduleKey]);

	if (!MDXContent) {
		return (
			<p className="text-sm text-muted-foreground">
				Unable to load article content.
			</p>
		);
	}

	return <MDXContent components={components} />;
}

interface MDXProps {
	/** Pre-built module key, e.g. `posts/example` (CSP-safe; no runtime eval). */
	readonly mdxModule?: string;
	/** Runtime eval fallback for tests only. */
	readonly code?: string;
}

function getHeadingText(children: React.ReactNode): string | undefined {
	if (typeof children !== "string") {
		return undefined;
	}
	const trimmed = children.trim();
	return trimmed || undefined;
}

async function copyHeadingPermalink(id: string) {
	const url = `${window.location.origin}${window.location.pathname}${window.location.search}#${id}`;
	try {
		await copyToClipboard(url);
		toast.success("Link copied");
		window.history.replaceState(null, "", `#${id}`);
	} catch {
		window.location.hash = id;
		toast.message("Use the URL in the address bar to share this section");
	}
}

function MdxHeading({
	level,
	className,
	children,
	...props
}: React.HTMLAttributes<HTMLHeadingElement> & { level: 1 | 2 | 3 | 4 | 5 }) {
	const { id: idProp, ...rest } = props;
	const slugger = useContext(HeadingSluggerContext);
	const displayChildren =
		typeof children === "string" ? children.trim() : children;
	const headingText = getHeadingText(children);
	const id =
		idProp ??
		(headingText && slugger ? slugifyHeading(slugger, headingText) : undefined);
	const Tag = `h${level}` as "h1" | "h2" | "h3" | "h4" | "h5";

	if (!id) {
		return (
			<Tag
				className={cn("flex flex-wrap items-baseline gap-x-2", className)}
				{...rest}
			>
				<span className="min-w-0 text-pretty">{displayChildren}</span>
			</Tag>
		);
	}

	const label =
		typeof displayChildren === "string"
			? `Copy link to “${displayChildren}”`
			: "Copy link";

	return (
		<Tag
			id={id}
			className={cn("group flex flex-wrap items-baseline gap-x-2", className)}
			{...rest}
		>
			<span className="min-w-0 text-pretty">{displayChildren}</span>
			<a
				href={`#${id}`}
				className={cn(
					"not-prose inline-flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground no-underline opacity-60 ring-ring/40 transition-colors hover:bg-muted/80 hover:text-primary hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background md:opacity-0 md:size-7 md:group-hover:opacity-100 md:group-focus-within:opacity-100",
				)}
				aria-label={label}
				title="Copy link to this section"
				onClick={(e) => {
					e.preventDefault();
					void copyHeadingPermalink(id);
				}}
			>
				<IconLink className="size-4 shrink-0" aria-hidden />
			</a>
		</Tag>
	);
}

const components = {
	h1: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
		<MdxHeading
			level={1}
			className={cn(
				"font-heading mt-8 scroll-m-24 text-3xl font-bold tracking-tight text-foreground text-balance sm:mt-10 sm:text-4xl sm:tracking-tighter",
				className,
			)}
			{...props}
		/>
	),
	h2: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
		<MdxHeading
			level={2}
			className={cn(
				"font-heading mt-12 scroll-m-24 border-b border-primary/20 pb-3 text-xl font-semibold tracking-tight text-foreground first:mt-0 sm:mt-14 sm:text-2xl dark:border-primary/30",
				className,
			)}
			{...props}
		/>
	),
	h3: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
		<MdxHeading
			level={3}
			className={cn(
				"font-heading mt-8 scroll-m-24 text-lg font-semibold tracking-tight text-foreground sm:mt-10 sm:text-xl",
				className,
			)}
			{...props}
		/>
	),
	h4: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
		<MdxHeading
			level={4}
			className={cn(
				"font-heading mt-7 scroll-m-24 text-base font-semibold tracking-tight text-foreground sm:mt-9 sm:text-lg",
				className,
			)}
			{...props}
		/>
	),
	h5: ({ className, ...props }: React.HTMLAttributes<HTMLHeadingElement>) => (
		<MdxHeading
			level={5}
			className={cn(
				"font-heading mt-6 scroll-m-24 text-sm font-semibold tracking-wide text-foreground/90 sm:mt-8 sm:text-base sm:tracking-tight",
				className,
			)}
			{...props}
		/>
	),
	p: ({ className, ...props }: React.HTMLAttributes<HTMLParagraphElement>) => (
		<p
			className={cn("leading-7 [&:not(:first-child)]:mt-6", className)}
			{...props}
		/>
	),
	ul: ({ className, ...props }: React.HTMLAttributes<HTMLUListElement>) => (
		<ul className={cn("ml-6 list-disc [&>li]:mt-2", className)} {...props} />
	),
	ol: ({ className, ...props }: React.HTMLAttributes<HTMLOListElement>) => (
		<ol className={cn("ml-6 list-decimal [&>li]:mt-2", className)} {...props} />
	),
	li: ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
		<li className={cn("mt-2", className)} {...props} />
	),
	blockquote: ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => (
		<blockquote
			className={cn(
				"mt-6 border-l-2 border-border pl-6 italic text-muted-foreground",
				className,
			)}
			{...props}
		/>
	),

	table: ({ children, ...props }: ComponentProps<"table">) => (
		<div className="my-6 w-full overflow-y-auto rounded-lg border border-border">
			<table className="w-full overflow-hidden" {...props}>
				{children}
			</table>
		</div>
	),
	th: ({ children, ...props }: ComponentProps<"th">) => (
		<th
			className="border-r border-border bg-muted px-6 py-3 text-left font-mono text-sm font-semibold text-foreground last:border-r-0"
			{...props}
		>
			{children}
		</th>
	),
	tr: ({ children, ...props }: ComponentProps<"tr">) => (
		<tr
			className="border-b border-border last:border-b-0 odd:bg-background even:bg-muted/50"
			{...props}
		>
			{children}
		</tr>
	),
	td: ({ children, ...props }: ComponentProps<"td">) => (
		<td
			className="border-r border-border px-6 py-4 text-sm text-foreground last:border-r-0"
			{...props}
		>
			{children}
		</td>
	),
	pre: ({
		children,
		className,
		...props
	}: React.HTMLAttributes<HTMLPreElement>) => {
		// biome-ignore lint/suspicious/noExplicitAny: rehype-pretty-code sets data-language on pre
		const isPrettyCode = (props as any)["data-language"] !== undefined;
		if (isPrettyCode) {
			return (
				<pre className={cn("m-0 bg-transparent p-0", className)} {...props}>
					{children}
				</pre>
			);
		}
		return (
			<div className="group relative my-4 overflow-hidden rounded-lg border border-border bg-card">
				<pre
					className={cn(
						"m-0 overflow-x-auto bg-transparent p-4 font-mono text-sm",
						className,
					)}
					{...props}
				>
					{children}
				</pre>
			</div>
		);
	},
	code: ({ className, ...props }: React.HTMLAttributes<HTMLElement>) => {
		// biome-ignore lint/suspicious/noExplicitAny: Weird types
		const isBlock = (props as any)["data-language"] !== undefined;
		return (
			<code
				className={cn(
					"font-mono text-[0.875em]",
					isBlock
						? "relative block w-full bg-transparent p-0"
						: "inline rounded bg-muted/70 px-1 py-0.5 font-medium text-foreground",
					className,
				)}
				{...props}
			/>
		);
	},
	a: CustomLink,
	figure: CodeBlockFigure,
};

const CustomComponent = {
	Step: ({ className, ...props }: React.ComponentProps<"h3">) => (
		<h3
			className={cn(
				"font-heading mt-8 scroll-m-20 text-xl font-semibold tracking-tight",
				className,
			)}
			{...props}
		/>
	),
	Steps: ({ ...props }) => (
		<div
			className="[&>h3]:step steps mb-12 ml-4 border-l pl-8 [counter-reset:step]"
			{...props}
		/>
	),
	Tweet: CustomTweet,
	Link: CustomLink,
	Note: Note,
	FAQ,
	OSTabs,
	OSTab,
	Footnote,
	FootnoteRef,
	Footnotes,
};

function useMergedMdxComponents() {
	return useMemo(() => ({ ...components, ...CustomComponent }), []);
}

function CustomMDXFromModule({ mdxModule }: { mdxModule: string }) {
	const slugger = useMemo(() => createHeadingSlugger(), []);
	const mergedComponents = useMergedMdxComponents();

	return (
		<HeadingSluggerContext.Provider value={slugger}>
			<MdxFromModule moduleKey={mdxModule} components={mergedComponents} />
		</HeadingSluggerContext.Provider>
	);
}

function CustomMDXFromCode({ code }: { code: string }) {
	const slugger = useMemo(() => createHeadingSlugger(), []);
	const mergedComponents = useMergedMdxComponents();
	const MDXContent = useMDXComponent(code);

	return (
		<HeadingSluggerContext.Provider value={slugger}>
			<MDXContent components={mergedComponents} />
		</HeadingSluggerContext.Provider>
	);
}

export default function CustomMDX({ mdxModule, code }: MDXProps) {
	if (mdxModule) {
		return <CustomMDXFromModule mdxModule={mdxModule} />;
	}

	return <CustomMDXFromCode code={code ?? ""} />;
}
